import { useState, useEffect } from "react";
import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import { getSpeakWithAccountExecutiveSchema } from "../../../../../../schemas/SpeakWithAccountExecutive";
import Input from "../../../../../custom-ui/new-ui/Input";
import PhoneInputWithCountry from "react-phone-number-input/react-hook-form";
import TextArea from "../../../../../custom-ui/new-ui/TextArea";
import ToastAlert from "../../../../../custom-ui/toast-alert/ToastAlert";
import { AccountExecutiveLeads } from "../../../../../../dataTypes/AccountExecutiveLeads";
import { useAuth } from "../../../../../../providers/auth";
import { createNewEntryForAccountExecutiveLeads } from "../../../../../../data/accountExecutiveLeads";
import { getUser } from "../../../../../../providers/database";

type TSpeakWithAccountExecutiveModalProps = {
  onCancel: () => void;
  onDone: () => void;
  type: "request" | "cancel";
  source:
  | "annualSubscriptionSignup"
  | "annualSubscriptionRenewal"
  | "annualSubscriptionCancel";
};

const SpeakWithAccountExecutiveModal = ({
  onCancel,
  onDone,
  type,
  source,
}: TSpeakWithAccountExecutiveModalProps) => {
  const { user } = useAuth();
  const [isInitialized, setIsInitialized] = useState(false);
  const [userData, setUserData] = useState(null);
  const employerId = user?.claims?.employerId;

  // @ts-ignore
  const [formSchema] = useState(getSpeakWithAccountExecutiveSchema());

  type FormValues = z.infer<
    ReturnType<typeof getSpeakWithAccountExecutiveSchema>
  >;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: userData?.name || "",
      email: userData?.email || "",
      phoneNumber: userData?.phone || "",
      message: "",
    },
    reValidateMode: "onBlur",
  });

  const {
    handleSubmit,
    setValue,
    getValues,
    control,
    reset,
    trigger,
    watch,
    formState: { errors, isSubmitting },
  } = form;

  const onSubmit = async ({ ...values }: FormValues) => {
    // const { fullName, email, phoneNumber, message, ...restValues } = values;
    const { message, ...restValues } = values;

    const fullName = userData?.name;
    const email = userData?.email;
    const phoneNumber = userData?.phone;

    const accountExecutiveLeadsData: AccountExecutiveLeads = {
      fullName,
      email,
      phoneNumber,
      message,
      employerId: employerId,
      createdAt: new Date(),
      type: type, // "request" | "cancel"
      source: source||"",
    };

    await createNewEntryForAccountExecutiveLeads(accountExecutiveLeadsData)
      .then(() => {
        toast.success("Your form has been submitted successfully");
        setTimeout(() => {
          onDone();
        }, 3000);
      })
      .catch(() => {
        toast.error("There was an error submitting the form");
      });
  };

  const setIntialValuesToForm = (userDataObj: any) => {
    if (!userDataObj || (userDataObj && !Object.keys(userDataObj).length))
      return;

    setValue("fullName", userDataObj?.name ?? "");
    setValue("email", userDataObj?.email ?? "");
    setValue("phoneNumber", userDataObj?.phone ?? "");
  };

  useEffect(() => {
    if (userData && !isInitialized) {
      console.log("values has been set")
      setIntialValuesToForm(userData);
      setIsInitialized(true);
    }
  }, [userData, isInitialized]);

  useEffect(() => {
    getUser(user.id).then((userDataObj) => {
      setUserData(userDataObj);
    });
  }, []);

  return (
    <>
      <div className="w-full">
        <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-5">
           
                  <Controller
                    control={control}
                    name="fullName"
                    render={(field) => (
                      <Input
                        {...field}
                        display="Full Name"
                        placeholder="Enter Full Name"
                        readOnly={false}
                        error={errors?.fullName?.message}
                        disabled={true}
                      // required={true}
                      />
                    )}
                  />

                  <Controller
                    control={control}
                    name="email"
                    render={(field) => (
                      <Input
                        {...field}
                        display="Email"
                        placeholder="Enter Email"
                        readOnly={false}
                        error={errors?.email?.message}
                        disabled={true}
                      // required={true}
                      />
                    )}
                  />

                  <div className="relative mb-7">
                    <PhoneInputWithCountry
                      className="peer mt-1 block w-full px-3 py-2 inp sm:text-sm outline-none ring-0 border-none text-[#707070]"
                      style={{ boxShadow: "none" }}
                      name="phoneNumber"
                      international
                      defaultCountry="US"
                      countryCallingCodeEditable={false}
                      control={control}
                      //onChange={async () => await trigger("phoneNumber")}
                      onBlur={async () => await trigger("phoneNumber")}
                      rules={{
                        required: "Phone number is required",
                        pattern: {
                          value: /^\+?[1-9]\d{1,14}$/,
                          message: "Invalid phone number",
                        },
                      }}
                      disabled={true}
                    />
                    <label
                      htmlFor="phoneNumber"
                      className="absolute -top-4 text-sm text-[16px] text-[#1E596D] bg-white px-1 transition-all duration-200 peer-placeholder-shown:top-2 peer-placeholder-shown:text-[#15415E] peer-placeholder-shown:text-sm peer-focus:-top-4 peer-focus:text-[#15415E] peer-focus:text-sm"
                    >
                      Phone Number
                      {/* <span className="mr-1 text-red-600">*</span> */}
                    </label>
                    {errors.phoneNumber && (
                      <p className="mt-1 text-sm text-left text-red-600">
                        {errors.phoneNumber.message}
                      </p>
                    )}
                  </div>
           

            <Controller
              control={control}
              name="message"
              render={(field) => (
                <>
                  <TextArea
                    {...field}
                    display="Message"
                    placeholder="Please write here"
                    labelClassName="text-[16px] text-[#1E596D] font-[400] mb-[10px]"
                    readOnly={false}
                    error={errors?.message?.message}
                    maxLength={500}
                    required={true}
                  />
                </>
              )}
            />
          </div>

          <div className="flex flex-row justify-end items-center gap-2.5 mt-4 pt-4 border-t border-[#E8EEF2]">
            <button
              className="h-[44px] px-6 rounded-full bg-[#15415E] text-white text-[14px] font-semibold hover:bg-[#1d5278] active:bg-[#123348] transition-all duration-200 disabled:bg-[#636b71] disabled:cursor-not-allowed"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
      <ToastAlert />
    </>
  );
};

export default SpeakWithAccountExecutiveModal;
